import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import '../../styles/pages.css';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ templateName: '', message: '' });
  const [deleteLoading, setDeleteLoading] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const entityId = user.entity;

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    try {
      setLoading(true);
      const { data } = await api.get(`/api/templates/${entityId}`);
      setTemplates(data);
      console.log('✓ Templates fetched:', data.length);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTemplate(e) {
    e.preventDefault();

    if (!formData.templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (!formData.message.trim()) {
      alert('Please enter a message');
      return;
    }

    if (formData.message.length > 160) {
      alert('Message exceeds 160 characters');
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post('/api/templates', {
        entityId,
        templateName: formData.templateName,
        message: formData.message,
      });

      console.log('✓ Template created:', data);
      setTemplates([...templates, data]);
      setFormData({ templateName: '', message: '' });
      setShowForm(false);
      alert('Template created successfully!');
    } catch (error) {
      console.error('Error creating template:', error);
      alert(error.response?.data?.message || 'Error creating template');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteTemplate(templateId) {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setDeleteLoading(templateId);
      await api.delete(`/api/templates/${templateId}`);

      setTemplates(templates.filter(t => t.id !== templateId));
      console.log('✓ Template deleted');
      alert('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(error.response?.data?.message || 'Error deleting template');
    } finally {
      setDeleteLoading(null);
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Message Templates</h1>
        <p>Create and manage reusable message templates</p>
      </div>

      <div className="templates-wrapper">
        {/* Templates List */}
        <div className="templates-list">
          <div className="list-header">
            <h2>Your Templates ({templates.length})</h2>
            <button
              className="btn-primary"
              onClick={() => setShowForm(!showForm)}
              disabled={loading}
            >
              {showForm ? '✕ Cancel' : '+ Create Template'}
            </button>
          </div>

          {/* Create Template Form */}
          {showForm && (
            <div className="create-template-form">
              <h3>Create New Template</h3>
              <form onSubmit={handleCreateTemplate}>
                <div className="form-group">
                  <label htmlFor="templateName">Template Name</label>
                  <input
                    id="templateName"
                    type="text"
                    value={formData.templateName}
                    onChange={(e) =>
                      setFormData({ ...formData, templateName: e.target.value })
                    }
                    placeholder="e.g., Welcome Message"
                    maxLength="50"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    Message Text
                    <span className="char-count">
                      {formData.message.length}/160
                    </span>
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder="Enter your message template..."
                    rows="4"
                    maxLength="160"
                    disabled={loading}
                  />
                  <p className="message-hint">
                    {160 - formData.message.length} characters remaining
                  </p>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner" style={{ marginRight: '0.5rem' }} />
                        Creating...
                      </>
                    ) : (
                      'Create Template'
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Templates Grid */}
          {loading && templates.length === 0 ? (
            <div className="loading-message">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="empty-state">
              <p>📝 No templates yet</p>
              <p className="empty-hint">Create your first template to get started</p>
            </div>
          ) : (
            <div className="templates-grid">
              {templates.map((template) => (
                <div key={template.id} className="template-card">
                  <div className="template-header">
                    <h4>{template.templateName}</h4>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteTemplate(template.id)}
                      disabled={deleteLoading === template.id}
                      title="Delete template"
                    >
                      {deleteLoading === template.id ? '...' : '×'}
                    </button>
                  </div>
                  <div className="template-message">
                    <p>{template.message}</p>
                  </div>
                  <div className="template-footer">
                    <span className="char-count-badge">
                      {template.message.length}/160 chars
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
